import React, { useMemo, useState, useEffect, useLayoutEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import PeopleTableSkeleton from "../../../../components/dashboardcomponents/People/PeopleTableSkeleton";
import PeopleTable from "../../../../components/dashboardcomponents/People/PeopleTable";
import AgentNameCell from "../../../../components/dashboardcomponents/People/Agents/AgentNameCell";
import AgentRolePill from "../../../../components/dashboardcomponents/People/Agents/AgentRolePill";
import AgentPresenceSelect from "../../../../components/dashboardcomponents/People/Agents/AgentPresenceSelect";
import AgentToolbar from "../../../../components/dashboardcomponents/People/Agents/AgentToolbar";
import AgentLegacyInviteModal from "../../../../components/dashboardcomponents/People/Agents/AgentLegacyInviteModal";
// import AgentAddModal from "../../../../components/dashboardcomponents/People/Agents/AgentAddModal";
import AgentDeleteModal from "../../../../components/dashboardcomponents/People/Agents/AgentDeleteModal";
import AgentSuspendModal from "../../../../components/dashboardcomponents/People/Agents/AgentSuspendModal";
import AgentDetailSidebar from "../../../../components/dashboardcomponents/People/Agents/AgentDetailSidebar";
import { buildAgentDetailView } from "../../../../components/dashboardcomponents/People/Agents/AgentDetail/agentDetailHelpers";
import { AGENT_OPEN_ADD_MODAL } from "../../../../components/dashboardcomponents/People/Agents/agentEvents";
import { parseListParam } from "../../../../components/dashboardcomponents/People/Customers/customersConfig";
import { AGENT_ROLE_FILTER_OPTIONS } from "../../../../constants/peopleData";
import { filterAgents, mergePeopleSearchParams } from "../../../../utils/peopleHelpers";
import {
  tabToAgentListStatus,
  lastActivityParamToDays,
} from "../../../../utils/agentMappers";
import useCustomerFormOptions from "../../../../hooks/useCustomerFormOptions";
import usePeoplePermissions from "../../../../hooks/usePeoplePermissions";
import useAgentPresenceSocket from "../../../../hooks/useAgentPresenceSocket";
import { useAnalyticsGroupId } from "../../../../hooks/useAnalyticsGroupId";
import useDebounce from "../../../../hooks/useDebounce";
import { peopleTableEllipsisSx } from "../../../../components/dashboardcomponents/People/peopleFormValidation";
import { logoutAgent } from "../../../../store/features/logoutAgent";
import { logoutAll } from "../../../../store/features/LogoutAll";
import {
  fetchAgentsList,
  fetchAgentById,
  updateAgentAcceptingChats,
  suspendAgent,
  unSuspendAgent,
  deleteAgent,
} from "../../../../store/features/agentsApi";
import "../../TicketManagment/style.css";

function apiMessage(payload, fallback) {
  return payload?.message || fallback;
}

export default function Agents() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedGroupId } = useAnalyticsGroupId();
  const tab = searchParams.get("tab") || "all";
  const search = searchParams.get("q") || "";
  const roleParam = searchParams.get("role") || "";
  const teamParam = searchParams.get("team") || "";
  const presenceParam = searchParams.get("fst") || "";
  const lastParam = searchParams.get("last") || "";

  const { list: rows, listLoading, detail, detailLoading, mutationLoading, lastFetchedListKey, cacheByListKey } = useSelector(
    (state) => state.peopleAgents
  );
  const currentUser = useSelector((state) => state.loginToken?.response?.user);
  const currentUserId = currentUser?.id || currentUser?._id || null;
  const { teamOptions, loading: optionsLoading } = useCustomerFormOptions();
  const perms = usePeoplePermissions();
  const [rowPatches, setRowPatches] = useState({});

  const [roleFilters, setRoleFilters] = useState(() => parseListParam(roleParam));
  const [teamFilters, setTeamFilters] = useState(() => parseListParam(teamParam));
  const [page, setPage] = useState(0);
  const [detailAgentId, setDetailAgentId] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const rowsPerPage = 10;

  const patchParams = (updates) => mergePeopleSearchParams(setSearchParams, updates);

  // Typing updates the input immediately but only reaches the URL (and the
  // list + tab-count requests it drives) once the query settles.
  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearchInput = useDebounce(searchInput, 400);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (debouncedSearchInput === search) return;
    patchParams({ q: debouncedSearchInput || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);

  const listQuery = useMemo(() => {
    const role =
      roleFilters.length > 0 ? roleFilters.join(",") : undefined;
    const team_id = teamFilters[0] || undefined;
    return {
      status: tabToAgentListStatus(tab),
      role,
      team_id,
      group_id: selectedGroupId || undefined,
      search: search || undefined,
      last_activity_days: lastActivityParamToDays(lastParam),
    };
  }, [tab, roleFilters, teamFilters, selectedGroupId, search, lastParam]);

  const listQueryKey = useMemo(
    () =>
      [
        listQuery.status || "all",
        listQuery.role || "",
        listQuery.team_id || "",
        listQuery.group_id || "",
        listQuery.search || "",
        listQuery.last_activity_days || "",
      ].join("::"),
    [listQuery]
  );

  useEffect(() => {
    setRowPatches({});
  }, [listQueryKey]);

  const sourceRows = cacheByListKey[listQueryKey] ?? rows;
  const isListStale = !cacheByListKey[listQueryKey] && listQueryKey !== lastFetchedListKey;
  const showListLoading = !selectedGroupId || listLoading || isListStale;

  const displayRows = useMemo(
    () =>
      sourceRows.map((row) => {
        const patch = rowPatches[row.id];
        return patch ? { ...row, ...patch } : row;
      }),
    [sourceRows, rowPatches]
  );

  const socketRef = useAgentPresenceSocket((agentId, updater) => {
    setRowPatches((prev) => ({
      ...prev,
      [agentId]: { ...(prev[agentId] || {}), ...updater() },
    }));
  });

  const patchDisplayRow = useCallback((agentId, patch) => {
    setRowPatches((prev) => ({
      ...prev,
      [agentId]: { ...(prev[agentId] || {}), ...patch },
    }));
  }, []);

  const reloadList = useCallback(() => {
    if (!selectedGroupId) return;
    dispatch(fetchAgentsList(listQuery));
  }, [dispatch, listQuery, selectedGroupId]);

  useEffect(() => {
    reloadList();
  }, [reloadList]);

  useEffect(() => {
    setRoleFilters(parseListParam(roleParam));
  }, [roleParam]);
  useEffect(() => {
    setTeamFilters(parseListParam(teamParam));
  }, [teamParam]);

  useLayoutEffect(() => {
    setPage(0);
  }, [tab, search, roleParam, teamParam, presenceParam, lastParam, selectedGroupId]);

  useEffect(() => {
    if (!detailAgentId) return;
    dispatch(fetchAgentById({ agentId: detailAgentId }));
  }, [detailAgentId, dispatch]);

  useEffect(() => {
    const openAdd = () => setInviteModalOpen(true);
    window.addEventListener(AGENT_OPEN_ADD_MODAL, openAdd);
    return () => window.removeEventListener(AGENT_OPEN_ADD_MODAL, openAdd);
  }, []);

  const syncRoleFilters = (next) => {
    setRoleFilters(next);
    patchParams({ role: next.length ? next.join(",") : undefined });
  };
  const syncTeamFilters = (next) => {
    setTeamFilters(next);
    patchParams({ team: next.length ? next.join(",") : undefined });
  };

  const filteredRows = useMemo(
    () => filterAgents(displayRows, { presence: presenceParam || undefined }),
    [displayRows, presenceParam]
  );

  const roleFilterOptions = useMemo(
    () => AGENT_ROLE_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    []
  );
  const toolbarTeamOptions = useMemo(
    () => teamOptions.map((t) => ({ value: t.id, label: t.label })),
    [teamOptions]
  );

  const sidebarAgent = useMemo(() => {
    if (!detailAgentId) return null;
    const detailId = detail?.id || detail?._id;
    if (detailId && String(detailId) === String(detailAgentId)) {
      return buildAgentDetailView(detail);
    }
    const row = displayRows.find((r) => String(r.id) === String(detailAgentId));
    if (row?.raw) return buildAgentDetailView(row.raw);
    return null;
  }, [detailAgentId, detail, displayRows]);

  const handlePresenceChange = async (row, nextPresence) => {
    if (
      nextPresence !== "accepting" &&
      nextPresence !== "notAccepting"
    ) {
      return;
    }
    if (
      row.presence === "offline" ||
      row.presence === "invitationSent" ||
      row.isJoined === false
    ) {
      return;
    }
    const accept_chat = nextPresence === "accepting";
    const result = await dispatch(
      updateAgentAcceptingChats({ agentId: row.id, accept_chat })
    );
    if (updateAgentAcceptingChats.fulfilled.match(result)) {
      patchDisplayRow(row.id, { presence: nextPresence });
      socketRef.current?.emit("update-status", row.id, accept_chat);
      toast.success(apiMessage(result.payload, "Status updated"));
    } else {
      toast.error(apiMessage(result.payload, "Failed to update status"));
    }
  };

  const handleAgentLogout = async (row) => {
    if (!row?.id || row.presence === "offline") return;
    const result = await dispatch(
      logoutAgent({
        requestHeaders: { agentId: row.id, adminId: currentUserId },
      })
    );
    if (result.payload?.status === 200) {
      socketRef.current?.emit("logout-agent", row.id);
      if (String(row.id) === String(currentUserId)) {
        dispatch(logoutAll());
      }
      patchDisplayRow(row.id, { presence: "offline" });
      toast.success("Agent logged out successfully");
    } else {
      toast.error(apiMessage(result.payload, "Failed to log out agent"));
    }
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      width: "minmax(220px, 1.4fr)",
      render: (row) => (
        <AgentNameCell name={row.name} jobTitle={row.jobTitle} avatar={row.avatar} />
      ),
    },
    { key: "email", label: "Email", width: "minmax(220px, 1.4fr)", render: (row) => (
      <Typography sx={{ color: "#405261", fontSize: 14, ...peopleTableEllipsisSx }}>
        {row.email}
      </Typography>
    ) },
    {
      key: "role",
      label: "Role",
      width: "120px",
      render: (row) => <AgentRolePill role={row.role} />,
    },
    { key: "chats", label: "Chats", width: "minmax(56px, 0.35fr)" },
    {
      key: "group",
      label: "Group",
      width: "minmax(100px, 0.55fr)",
      cellPr: 2.5,
      render: (row) => (
        <Typography
          sx={{
            color: "#405261",
            fontSize: 14,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
            width: "100%",
          }}
        >
          {row.group}
        </Typography>
      ),
    },
    {
      key: "status",
      label: "Status",
      width: "184px",
      cellPr: 2,
      render: (row) => {
        const rowPerms = perms.agents.forAgent(row.id, row.roleName);
        const canManagePresence =
          rowPerms.canChangeStatus &&
          row.isJoined !== false &&
          row.presence !== "offline" &&
          row.presence !== "invitationSent" &&
          row.presence !== "suspended";
        return (
          <Box sx={{ maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
            <AgentPresenceSelect
              value={row.presence}
              onChange={(val) => handlePresenceChange(row, val)}
              onLogout={canManagePresence ? () => handleAgentLogout(row) : undefined}
              disabled={
                !rowPerms.canChangeStatus ||
                row.presence === "offline" ||
                row.presence === "invitationSent" ||
                row.isJoined === false
              }
            />
          </Box>
        );
      },
    },
    {
      key: "lastActivity",
      label: "Last Activity",
      width: "minmax(120px, 0.55fr)",
      cellPl: 1.5,
      cellPr: 0.5,
      render: (row) => (
        <Typography
          sx={{
            color: "#405261",
            fontSize: 14,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          {row.lastActivity}
        </Typography>
      ),
    },
  ];

  const actionMenu = useMemo(
    () => [
      { key: "view", label: "View Detail" },
      {
        key: "edit",
        label: "Edit Agent",
        hidden: (row) => {
          if (!row || row.presence === "suspended" || tab === "suspended") return true;
          return !perms.agents.forAgent(row.id, row.roleName).canEdit;
        },
      },
      {
        key: "activate",
        label: "Active Agent",
        hidden: (row) => {
          if (!row || (row.presence !== "suspended" && tab !== "suspended")) return true;
          return !perms.agents.forAgent(row.id, row.roleName).canActivate;
        },
      },
      {
        key: "suspend",
        label: "Suspend Agent",
        hidden: (row) => {
          if (!row || row.presence === "suspended" || tab === "suspended") return true;
          return !perms.agents.forAgent(row.id, row.roleName).canSuspend;
        },
      },
      {
        key: "delete",
        label: "Delete Agent",
        danger: true,
        hidden: (row) => !row || !perms.agents.forAgent(row.id, row.roleName).canDelete,
      },
    ],
    [perms, tab]
  );

  const onAction = (type, row) => {
    if (type === "view") {
      setDetailAgentId(row.id);
    } else if (type === "edit") {
      navigate(`/people/agents/${row.id}/edit`);
    } else if (type === "delete") {
      setDeleteTarget(row);
    } else if (type === "suspend") {
      setSuspendTarget(row);
    } else if (type === "activate") {
      dispatch(unSuspendAgent({ agentId: row.id })).then((result) => {
        if (unSuspendAgent.fulfilled.match(result)) {
          toast.success(apiMessage(result.payload, "Agent activated"));
          reloadList();
        } else {
          toast.error(apiMessage(result.payload, "Failed to activate agent"));
        }
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const agentId = deleteTarget.id;
    const isSelf = String(agentId) === String(currentUserId);

    // Force-logout logged-in agent before delete (same API as People Agents logout)
    if (deleteTarget.presence !== "offline" && deleteTarget.presence !== "invitationSent") {
      const logoutResult = await dispatch(
        logoutAgent({
          requestHeaders: { agentId, adminId: currentUserId },
        })
      );
      if (logoutResult.payload?.status === 200) {
        socketRef.current?.emit("logout-agent", agentId);
      }
    }

    const result = await dispatch(deleteAgent({ agentId }));
    if (deleteAgent.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, "User deleted successfully"));
      setDeleteTarget(null);
      if (isSelf) {
        dispatch(logoutAll());
      } else {
        reloadList();
      }
    } else {
      toast.error(apiMessage(result.payload, "Failed to delete agent"));
    }
  };

  const confirmSuspend = async () => {
    if (!suspendTarget) return;
    const result = await dispatch(suspendAgent({ agentId: suspendTarget.id }));
    if (suspendAgent.fulfilled.match(result)) {
      socketRef.current?.emit("logout-agent", suspendTarget.id);
      patchDisplayRow(suspendTarget.id, { presence: "suspended", isSuspended: true });
      toast.success(apiMessage(result.payload, "Agent suspended"));
      setSuspendTarget(null);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to suspend agent"));
    }
  };

  /*
  const handleAddAgent = async (form) => {
    const body = buildAgentInvitePayload({
      email: form.email.trim(),
      roleName: form.roleName,
      groupIds: form.groupId ? [form.groupId] : undefined,
      teamIds: form.teamId ? [form.teamId] : undefined,
    });
    const result = await dispatch(inviteAgent(body));
    if (inviteAgent.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, "Invitation sent"));
      setAddModalOpen(false);
      reloadList();
    } else {
      toast.error(apiMessage(result.payload, "Failed to add agent"));
    }
  };
  */

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <AgentToolbar
        searchQuery={searchInput}
        onSearchChange={setSearchInput}
        roleFilters={roleFilters}
        teamFilters={teamFilters}
        onRoleFiltersChange={syncRoleFilters}
        onTeamFiltersChange={syncTeamFilters}
        onClearAll={() => {
          syncRoleFilters([]);
          syncTeamFilters([]);
        }}
        roleOptions={roleFilterOptions}
        teamOptions={toolbarTeamOptions}
        optionsLoading={optionsLoading}
      />

      <Box
        className="Ticket-container-render"
        sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, width: "100%" }}
      >
        {showListLoading ? (
          <PeopleTableSkeleton columns={columns} leadWithAvatar />
        ) : (
          <PeopleTable
            columns={columns}
            rows={filteredRows}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangePage={(p) => setPage(p)}
            actionMenu={actionMenu}
            onAction={onAction}
            onRowClick={(row) => setDetailAgentId(row.id)}
            entityLabel="Agents"
            selectable={false}
            horizontalBleed={24}
          />
        )}
      </Box>

      <AgentDetailSidebar
        agent={sidebarAgent}
        loading={detailLoading}
        onClose={() => setDetailAgentId(null)}
        onViewDetail={() => detailAgentId && navigate(`/people/agents/${detailAgentId}`)}
        onEdit={() => detailAgentId && navigate(`/people/agents/${detailAgentId}/edit`)}
        canEdit={
          sidebarAgent
            ? perms.agents.forAgent(sidebarAgent.id, sidebarAgent.roleName).canEdit
            : false
        }
      />

      <AgentLegacyInviteModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={reloadList}
      />

      {/*
      <AgentAddModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onConfirm={handleAddAgent}
        groupOptions={groupOptions}
        teamOptions={teamOptions}
        optionsLoading={optionsLoading}
        submitting={mutationLoading}
      />
      */}

      <AgentDeleteModal
        open={Boolean(deleteTarget)}
        agentName={deleteTarget?.name}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        loading={mutationLoading}
      />

      <AgentSuspendModal
        open={Boolean(suspendTarget)}
        agentName={suspendTarget?.name}
        onClose={() => setSuspendTarget(null)}
        onConfirm={confirmSuspend}
        loading={mutationLoading}
      />
    </Box>
  );
}
